import React, { useEffect, useState } from "react";
import { capitalizeFirstLetter } from "../../../utils/letters";
import { formatDate } from "../../../utils/date";
import {
	listMyBatches,
	listBatchStudents,
	updateStudent,
	setStudentSimplePassword,
	setBatchPasswords,
} from "../../../api/company";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyEditErrors = {
	first_name: "",
	last_name: "",
	email: "",
};

const emptyEditTouched = {
	first_name: false,
	last_name: false,
	email: false,
};

function validateStudentField(name, value) {
	const val = String(value || "").trim();
	if (name === "first_name") return val ? "" : "El nombre es obligatorio.";
	if (name === "last_name") return val ? "" : "El apellido es obligatorio.";
	if (name === "email") {
		if (!val) return "";
		return EMAIL_REGEX.test(val) ? "" : "El correo debe tener un formato valido.";
	}
	return "";
}

function mapApiDetailsToFieldErrors(details) {
	if (!details || typeof details !== "object") return null;
	return {
		first_name: Array.isArray(details.first_name) ? String(details.first_name[0] || "") : "",
		last_name: Array.isArray(details.last_name) ? String(details.last_name[0] || "") : "",
		email: Array.isArray(details.email) ? String(details.email[0] || "") : "",
	};
}
export default function MyStudentsPage() {
	const [batches, setBatches] = useState([]);
	const [selectedBatchId, setSelectedBatchId] = useState("");
	const [students, setStudents] = useState([]);
	const [loadingBatches, setLoadingBatches] = useState(true);
	const [loadingStudents, setLoadingStudents] = useState(false);
	const [togglingId, setTogglingId] = useState(null);
	const [error, setError] = useState(null);
	const [success, setSuccess] = useState(null);
	const [editingId, setEditingId] = useState(null);
	const [editForm, setEditForm] = useState({ first_name: "", last_name: "", email: "" });
	const [editErrors, setEditErrors] = useState(emptyEditErrors);
	const [editTouched, setEditTouched] = useState(emptyEditTouched);
	const [passwordResult, setPasswordResult] = useState(null);
	const [loadingPasswords, setLoadingPasswords] = useState(false);

	useEffect(() => {
		(async () => {
			setLoadingBatches(true);
			setError(null);
			try {
				const data = await listMyBatches();
				setBatches(Array.isArray(data) ? data : []);
				setSelectedBatchId((prev) => {
					if (prev) return prev;
					return data?.length ? String(data[0].id) : "";
				});
			} catch (e) {
				setError(e?.response?.data?.error || "No se pudieron cargar los lotes.");
			} finally {
				setLoadingBatches(false);
			}
		})();
	}, []);

	useEffect(() => {
		if (!selectedBatchId) {
			setStudents([]);
			return;
		}
		let cancelled = false;
		(async () => {
			setLoadingStudents(true);
			setError(null);
			try {
				const data = await listBatchStudents(Number(selectedBatchId));
				if (!cancelled) setStudents(Array.isArray(data) ? data : []);
			} catch (e) {
				if (!cancelled) {
					setError(e?.response?.data?.error || "No se pudieron cargar los estudiantes.");
					setStudents([]);
				}
			} finally {
				if (!cancelled) setLoadingStudents(false);
			}
		})();
		return () => { cancelled = true; };
	}, [selectedBatchId]);

	const handleToggleActive = async (student) => {
		setTogglingId(student.id);
		setError(null);
		setSuccess(null);
		try {
			const newActive = !student.is_active;
			await updateStudent(student.id, { is_active: newActive });
			setStudents((prev) => prev.map((s) => (s.id === student.id ? { ...s, is_active: newActive } : s)));
		} catch (e) {
			setError(e?.response?.data?.error || "No se pudo actualizar.");
		} finally {
			setTogglingId(null);
		}
	};

	const startEdit = (s) => {
		setEditingId(s.id);
		setEditForm({
			first_name: s.first_name || "",
			last_name: s.last_name || "",
			email: s.email || "",
		});
		setEditErrors(emptyEditErrors);
		setEditTouched(emptyEditTouched);
		setError(null);
		setSuccess(null);
	};

	const cancelEdit = () => {
		setEditingId(null);
		setEditForm({ first_name: "", last_name: "", email: "" });
		setEditErrors(emptyEditErrors);
		setEditTouched(emptyEditTouched);
	};

	const handleEditChange = (field, value) => {
		setEditForm((prev) => ({ ...prev, [field]: value }));
	};

	const handleEditBlur = (field) => {
		setEditTouched((prev) => ({ ...prev, [field]: true }));
		setEditErrors((prev) => ({
			...prev,
			[field]: validateStudentField(field, editForm[field]),
		}));
	};

	const saveEdit = async () => {
		if (!editingId) return;
		setError(null);
		setSuccess(null);
		const nextErrors = {
			first_name: validateStudentField("first_name", editForm.first_name),
			last_name: validateStudentField("last_name", editForm.last_name),
			email: validateStudentField("email", editForm.email),
		};
		setEditTouched({ first_name: true, last_name: true, email: true });
		setEditErrors(nextErrors);
		if (Object.values(nextErrors).some(Boolean)) {
			setError(Object.values(nextErrors).find(Boolean));
			return;
		}
		try {
			const payload = {
				first_name: (editForm.first_name || "").trim(),
				last_name: (editForm.last_name || "").trim(),
				email: (editForm.email || "").trim(),
			};
			const data = await updateStudent(editingId, payload);
			setStudents((prev) => prev.map((s) => (s.id === editingId ? { ...s, ...data } : s)));
			setSuccess("Datos actualizados.");
			setEditingId(null);
			setEditErrors(emptyEditErrors);
			setEditTouched(emptyEditTouched);
		} catch (e) {
			const mappedErrors = mapApiDetailsToFieldErrors(e?.response?.data?.details);
			if (mappedErrors) {
				setEditErrors(mappedErrors);
				setEditTouched({ first_name: true, last_name: true, email: true });
			}
			setError(e?.response?.data?.error || "No se pudo guardar.");
		}
	};

	const handleSetPassword = async (student) => {
		setError(null);
		setPasswordResult(null);
		try {
			const data = await setStudentSimplePassword(student.id);
			setPasswordResult({
				nombre: `${student.first_name} ${student.last_name}`,
				rut: student.rut,
				temp_password: data.temp_password,
				username: data.username,
			});
		} catch (e) {
			setError(e?.response?.data?.error || "No se pudo asignar la contraseña.");
		}
	};

	const handleSetBatchPasswords = async () => {
		if (!selectedBatchId) return;
		setLoadingPasswords(true);
		setError(null);
		setPasswordResult(null);
		try {
			const data = await setBatchPasswords(Number(selectedBatchId));
			setPasswordResult({
				bulk: true,
				updated: data.updated,
				passwords: data.passwords || [],
			});
			setSuccess(`Se asignó contraseña a ${data.updated} estudiante(s). Deben cambiarla al primer ingreso. Puedes descargar el Excel con todas las contraseñas.`);
		} catch (e) {
			setError(e?.response?.data?.error || "No se pudieron asignar las contraseñas.");
		} finally {
			setLoadingPasswords(false);
		}
	};

	return (
		<>
			<div className="p-4 backoffice-tables flex-grow-1 overflow-auto font-n">
				<div>
					<h2 className="portal-emp-title">Mis estudiantes</h2>
					<p className="portal-emp-intro">
						Solo ves los estudiantes de los lotes que tú cargaste. Puedes editar datos, asignar contraseña simple (inicial + apellidos + RUT) y habilitar/deshabilitar. Las contraseñas son fáciles de comunicar; al primer ingreso el estudiante deberá cambiarla.
					</p>

					{error && (
						<div className="alert alert-danger portal-emp-alert" role="alert">
							{error}
						</div>
					)}
					{success && (
						<div className="alert alert-success portal-emp-alert" role="alert">
							{success}
						</div>
					)}
					{passwordResult && (
						<div className="description-txt mb-4" role="alert">
							{passwordResult.bulk ? (
								<>
									<div className="d-flex justify-content-between align-items-center mb-2">
										<strong>Contraseñas asignadas ({passwordResult.updated}):</strong>
										<span className="small text-muted">Anota o copia estas contraseñas antes de cerrar esta alerta.</span>
									</div>

									{/* Reciclamos tu clase table-wrapper-scroll, pero forzamos un alto menor (aprox 5 filas) */}
									<div className="table-responsive table-wrapper-scroll shadow-sm border rounded" style={{ maxHeight: "250px" }}>
										<table className="table  table-hover bg-white mb-0 align-middle">
											<thead className="table-light">
												<tr>
													<th>Nombre</th>
													<th>Usuario</th>
													<th>RUT</th>
													<th>Contraseña Temporal</th>
													<th>Acción</th>
												</tr>
											</thead>
											<tbody>
												{(passwordResult.passwords || []).map((p, i) => (
													<tr key={i}>
														<td><span>{capitalizeFirstLetter(p.nombre)}</span></td>
														<td><span><strong>{p.username}</strong></span></td>
														<td><span>{p.rut}</span></td>
														<td><code className="fs-6 px-2 py-1 rounded text-dark">{p.temp_password}</code></td>
														<td>
															<button
																type="button"
																className="admin-btn"
																onClick={() => {
																	const textToCopy = `Nombre: ${capitalizeFirstLetter(p.nombre)}\nUsuario: ${p.username}\nRUT: ${p.rut}\nContraseña: ${p.temp_password}`;

																	// Lo mandamos al portapapeles
																	navigator.clipboard.writeText(textToCopy);
																}}
																title="Copiar datos del estudiante"
															>
																<i className="ri-clipboard-line me-1 d-md-none d-inline"></i> <span className="d-none d-md-inline">Copiar</span>
															</button>
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								</>
							) : (
								<div className="d-flex justify-content-between align-items-center">
									<div>
										<span className="text-capitalize">{passwordResult.nombre}</span>
										<br />
										<span>RUT: {passwordResult.rut}</span>
										<br />
										<span>Usuario: <strong>{passwordResult.username}</strong></span>
										<br />
										<span className=" d-inline-block">
											Contraseña: <code className="fs-6 px-2 py-1 rounded text-dark">{passwordResult.temp_password}</code>
										</span>
										<p className="small mb-0 mt-1 text-muted">Debe cambiarla al primer ingreso.</p>
									</div>
									<div>
										<button
											type="button"
											className="admin-btn"
											onClick={() => {
												const textToCopy = `Nombre: ${capitalizeFirstLetter(passwordResult.nombre)}\nUsuario: ${passwordResult.username}\nRUT: ${passwordResult.rut}\nContraseña: ${passwordResult.temp_password}`;

												navigator.clipboard.writeText(textToCopy);
											}}
											title="Copiar datos del estudiante"
										>
											<i className="ri-clipboard-line me-1 d-md-none d-inline"></i> <span className="d-none d-md-inline">Copiar</span>
										</button>
									</div>
								</div>
							)}
						</div>
					)}

					<section className="card card-body-updown">
						<div className="card-body">
							<h5 className="text-nowrap mb-2">Elegir lote:</h5>
							<select
								className="form-select"
								value={selectedBatchId}
								onChange={(e) => setSelectedBatchId(e.target.value)}
								disabled={loadingBatches}
							>
								<option value="">Selecciona un lote</option>
								{batches.map((b) => (
									<option key={b.id} value={b.id}>
										ID {b.id} — {b.file_name} ({formatDate(b.created_at) || b.created_at})
									</option>
								))}
							</select>
							{!loadingBatches && batches.length === 0 && (
								<p className="text-muted mt-2 mb-0">No tienes lotes. Sube un Excel en Carga de estudiantes.</p>
							)}
						</div>
					</section>

					{selectedBatchId && students.length > 0 && (
						<section className="card mt-3 card-table">
							<div className="card-body">
								<div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
									<h5 className="mb-0">Estudiantes del lote</h5>
									<div className="d-flex flex-wrap gap-2 bloc-btn-emp">
										<button
											type="button"
											className="adm-passw-btn"
											onClick={handleSetBatchPasswords}
											disabled={loadingPasswords}
										>
											<i className="ri-key-2-line me-md-1"></i>
											{loadingPasswords ? (
												<span>Asignando…</span>
											) : (
												<>
													<span className="d-inline d-md-none"> A todo el lote</span>
													<span className="d-md-inline d-none"> Asignar contraseñas a todo el lote</span>
												</>

											)}
										</button>
									</div>
								</div>
								{loadingStudents ? (
									<p className="text-muted">Cargando...</p>
								) : (
									<div className="table-responsive table-wrapper-scroll">
										<table className="table table-hover align-middle mb-0">
											<thead className="table-light">
												<tr>
													<th>Nombre</th>
													<th>RUT</th>
													<th>Correo</th>
													<th>Estado</th>
													<th>Acciones</th>
												</tr>
											</thead>
											<tbody>
												{students.map((s) => (
													<tr key={s.id}>
														{editingId === s.id ? (
															<>
																<td colSpan={4}>
																	<div className="mystu-emp">
																		<div className="col-12">
																			<p>Nombres:</p>
																			<input
																				className={`form-control form-control-sm ${editTouched.first_name && editErrors.first_name ? "is-invalid" : ""}`}
																				value={editForm.first_name}
																				placeholder="Ej: Nombre"
																				onChange={(e) => handleEditChange("first_name", e.target.value)}
																				onBlur={() => handleEditBlur("first_name")}
																			/>
																			{editTouched.first_name && editErrors.first_name && (
																				<div className="invalid-feedback d-block">{editErrors.first_name}</div>
																			)}
																		</div>
																		<div className="col-12">
																			<p>Apellidos:</p>
																			<input
																				className={`form-control form-control-sm ${editTouched.last_name && editErrors.last_name ? "is-invalid" : ""}`}
																				value={editForm.last_name}
																				placeholder="Ej: Apellido"
																				onChange={(e) => handleEditChange("last_name", e.target.value)}
																				onBlur={() => handleEditBlur("last_name")}
																			/>
																			{editTouched.last_name && editErrors.last_name && (
																				<div className="invalid-feedback d-block">{editErrors.last_name}</div>
																			)}
																		</div>
																		<div className="col-12">
																			<p>Email:</p>
																			<input
																				type="email"
																				className={`form-control form-control-sm ${editTouched.email && editErrors.email ? "is-invalid" : ""}`}
																				value={editForm.email}
																				placeholder="Ej: nombre@correo.com"
																				onChange={(e) => handleEditChange("email", e.target.value)}
																				onBlur={() => handleEditBlur("email")}
																			/>
																			{editTouched.email && editErrors.email && (
																				<div className="invalid-feedback d-block">{editErrors.email}</div>
																			)}
																		</div>
																	</div>
																</td>
																<td colSpan={1}>
																	<div className="mystu-emp-btn">
																		<button type="button" className="adm-save-btn me-1" onClick={saveEdit}><span>Guardar</span></button>
																		<button type="button" className="adm-del-btn" onClick={cancelEdit}><span>Cancelar</span></button>
																	</div>

																</td>
															</>
														) : (
															<>
																<td>{s.first_name} {s.last_name}</td>
																<td>{s.rut}</td>
																<td>{s.email || "—"}</td>
																<td>
																	<span>
																		{s.is_active ? "Sí" : "No"}
																	</span>
																</td>
																<td>
																	<div className="d-flex flex-wrap gap-1">
																		<button type="button" className="adm-upd-btn" onClick={() => startEdit(s)} title="Editar"><i className="ri-edit-fill"></i></button>
																		<button type="button" className="adm-passw-btn" onClick={() => handleSetPassword(s)} title="Asignar contraseña simple"><i className="ri-key-2-line"></i></button>
																		<button
																			type="button"
																			className={s.is_active ? "adm-del-btn" : "adm-save-btn"}
																			disabled={togglingId === s.id}
																			onClick={() => handleToggleActive(s)}
																		>
																			<i className="ri-close-circle-line d-inline d-md-none"></i>
																			<span className="d-none d-md-inline">
																				{s.is_active ? "Deshabilitar" : "Habilitar"}
																			</span>
																		</button>
																	</div>
																</td>
															</>
														)}
													</tr>
												))}
											</tbody>
										</table>
									</div>
								)}
							</div>
						</section>
					)}

					{selectedBatchId && !loadingStudents && students.length === 0 && (
						<section className="portal-emp-card mt-3">
							<p className="text-muted mb-0">No hay estudiantes en este lote.</p>
						</section>
					)}
				</div>
			</div>
		</>
	);
}
